import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import RosterBuilder from "@/pages/RosterBuilder";
import TeamRosters from "@/pages/TeamRosters";
import Tasks from "@/pages/Tasks";
import Holidays from "@/pages/Holidays";
import Approvals from "@/pages/Approvals";
import Notifications from "@/pages/Notifications";
import UsersSettings from "@/pages/settings/UsersSettings";
import RolesSettings from "@/pages/settings/RolesSettings";
import DepartmentsSettings from "@/pages/settings/DepartmentsSettings";
import EmailSettings from "@/pages/settings/EmailSettings";
import ChecklistsSettings from "@/pages/settings/ChecklistsSettings";
import ChecklistBuilder from "@/pages/settings/ChecklistBuilder";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import { can } from "@/lib/colors";

const Splash = () => (
  <div className="min-h-screen grid place-items-center" data-testid="app-loading">
    <img src="/logo.png" alt="Logo" className="w-16 h-16 animate-pulse" />
  </div>
);

const Protected = ({ perms, children }) => {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (perms && !can(user, ...perms)) return <Navigate to="/" replace />;
  return <AppShell>{children}</AppShell>;
};

const SettingsHome = () => {
  const { user } = useAuth();
  if (can(user, "manage_users")) return <Navigate to="/settings/users" replace />;
  if (can(user, "manage_settings")) return <Navigate to="/settings/roles" replace />;
  return <Navigate to="/" replace />;
};

const LoginRoute = () => {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  return user ? <Navigate to="/" replace /> : <Login />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/projects" element={<Protected><Projects /></Protected>} />
            <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
            <Route path="/settings/email" element={<Protected perms={["manage_settings"]}><EmailSettings /></Protected>} />
            <Route path="/settings/checklists" element={<Protected perms={["manage_settings"]}><ChecklistsSettings /></Protected>} />
            <Route path="/settings/checklists/:id" element={<Protected perms={["manage_settings"]}><ChecklistBuilder /></Protected>} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/roster" element={<Protected perms={["own_roster"]}><RosterBuilder /></Protected>} />
            <Route path="/team" element={<Protected perms={["view_team"]}><TeamRosters /></Protected>} />
            <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
            <Route path="/holidays" element={<Protected><Holidays /></Protected>} />
            <Route path="/approvals" element={<Protected perms={["edit_rosters", "approve_leave"]}><Approvals /></Protected>} />
            <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
            <Route path="/settings" element={<Protected><SettingsHome /></Protected>} />
            <Route path="/settings/users" element={<Protected perms={["manage_users"]}><UsersSettings /></Protected>} />
            <Route path="/settings/roles" element={<Protected perms={["manage_settings"]}><RolesSettings /></Protected>} />
            <Route path="/settings/departments" element={<Protected perms={["manage_settings"]}><DepartmentsSettings /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}

export default App;
